import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Clock, 
  Eye,
  ArrowLeft,
  Share2,
  Twitter,
  Facebook,
  Linkedin,
  Link as LinkIcon,
  Loader2,
  User
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShareMenu, setShowShareMenu] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/posts/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Failed to fetch post:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post?.title || '';

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard!');
    setShowShareMenu(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00FF94] animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Post not found</h1>
          <Link to="/blog">
            <Button className="btn-primary">Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="blog-post-page">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link to="/blog" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </Link>

        {/* Header */}
        <header className="mb-8">
          <Badge className="bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20 capitalize mb-4">
            {post.category}
          </Badge>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6 leading-tight">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {formatDate(post.created_at)}
            </span>
            <span className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              {post.views?.toLocaleString()} views
            </span>
          </div>
        </header>

        {/* Featured Image */}
        <div className="aspect-video rounded-xl overflow-hidden mb-8 border border-[#262626]">
          <img 
            src={post.image_url || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=1200'} 
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Share Buttons */}
        <div className="flex items-center gap-4 mb-8 p-4 bg-[#0a0a0a] border border-[#262626] rounded-xl">
          <span className="text-sm text-gray-400">Share this article:</span>
          <div className="flex items-center gap-2">
            <a 
              href={shareLinks.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-lg bg-[#171717] border border-[#262626] flex items-center justify-center text-gray-400 hover:text-[#1DA1F2] hover:border-[#1DA1F2]/30 transition-colors"
              data-testid="share-twitter"
            >
              <Twitter className="w-4 h-4" />
            </a>
            <a 
              href={shareLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-lg bg-[#171717] border border-[#262626] flex items-center justify-center text-gray-400 hover:text-[#4267B2] hover:border-[#4267B2]/30 transition-colors"
              data-testid="share-facebook"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <a 
              href={shareLinks.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-lg bg-[#171717] border border-[#262626] flex items-center justify-center text-gray-400 hover:text-[#0A66C2] hover:border-[#0A66C2]/30 transition-colors"
              data-testid="share-linkedin"
            >
              <Linkedin className="w-4 h-4" />
            </a>
            <button
              onClick={copyLink}
              className="w-9 h-9 rounded-lg bg-[#171717] border border-[#262626] flex items-center justify-center text-gray-400 hover:text-[#00FF94] hover:border-[#00FF94]/30 transition-colors"
              data-testid="share-copy"
            >
              <LinkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-lg max-w-none">
          <div 
            className="text-gray-300 leading-relaxed space-y-6"
            style={{ whiteSpace: 'pre-wrap' }}
          >
            {post.content}
          </div>
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-8 pt-8 border-t border-[#262626]">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-500">Tags:</span>
              {post.tags.map((tag, index) => (
                <Badge 
                  key={index}
                  className="bg-[#171717] text-gray-400 border-[#262626] hover:border-[#00FF94]/30 cursor-pointer"
                >
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 p-8 bg-[#0a0a0a] border border-[#262626] rounded-xl text-center">
          <h3 className="font-heading font-bold text-xl text-white mb-3">
            Protect Your Privacy Today
          </h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Get your personalized privacy score and step-by-step protection guides.
          </p>
          <Link to="/register">
            <Button className="btn-primary" data-testid="blog-cta">
              Get Started Free
            </Button>
          </Link>
        </div>
      </article>
    </div>
  );
}
